/*
 *   File : restricted-access.js
 *   Description : Shown when a user in restricted mode (v2 onboarding session — has an
 *                 onboarding_token, no mPIN yet) taps a restricted action (invest / SIP / etc.).
 *                 Content is dynamic based on how far the KYC has progressed.
 *   Version : v1.0
 */

import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Modal } from "bootstrap";
import { toast } from "react-toastify";

import { GradientButton } from "../CTA";
import { Icon } from "../Icon";
import { logOut } from "../../Helper";
import { isOnboardingSession } from "../../Helper/onboarding";

export const RESTRICTED_ACCESS_MODAL_ID = "restricted-access";

// Imperatively open the modal from anywhere (it is mounted once, in App.js, for onboarding sessions).
export const openRestrictedAccessModal = () => {
    const el = document.getElementById(RESTRICTED_ACCESS_MODAL_ID);
    if (el) {
        Modal.getOrCreateInstance(el).show();
    }
};

// Single entry point for the "can't do this in restricted mode" gate. An onboarding user gets the
// rich modal; anyone else hitting the kyc_step<11 gate keeps the original toast.
export const showRestrictedInvestPrompt = () => {
    if (isOnboardingSession()) {
        openRestrictedAccessModal();
    } else {
        toast.dismiss();
        toast.error("Complete KYC to start investing", { type: "error" });
    }
};

export default function RestrictedAccessModal() {

    const navigate = useNavigate();
    const KYC_STEP = useSelector(state => state.generalData.KYC_PROGRESS_DATA);

    // Three restricted-mode states, from the KYC_MAP ladder (branch on signup_progress_text, not
    // kyc_step alone — rank 10 is REJECTED, not "done"):
    //   rejected  -> ADMIN_REJECTED / KYC_REJECTED (rank 10)
    //   submitted -> eSign submitted / account being created (ranks 9, 11, 12) -> only mPIN pending
    //   else      -> still completing KYC (rank < 9)
    const kycStep = KYC_STEP?.kyc_step ?? 0;
    const isRejected = ['ADMIN_REJECTED', 'KYC_REJECTED'].includes(KYC_STEP?.signup_progress_text);
    const kycSubmitted = !isRejected && kycStep >= 9;

    const _hide = () => {
        const el = document.getElementById(RESTRICTED_ACCESS_MODAL_ID);
        const instance = el && Modal.getInstance(el);
        if (instance) {
            instance.hide();
        }
    };

    const _handleCompleteKyc = () => {
        _hide();
        navigate('/kyc');
    };

    const _handleLogout = () => {
        logOut();
    };

    return (
        <div className="modal fade"
            id={RESTRICTED_ACCESS_MODAL_ID}
            tabIndex="-1"
            aria-labelledby={RESTRICTED_ACCESS_MODAL_ID}
            aria-hidden="true"
            data-bs-backdrop="static">
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content position-relative text-center p-sm-5 p-4">

                    <Icon icon="close"
                        size={24}
                        data-bs-dismiss="modal"
                        className="position-absolute cursor-pointer e-modal-close-btn"
                        style={{ right: "20px", top: "20px" }} />

                    <img src={require("../../Assets/Images/dashboard/unlock-icon.png")}
                        alt="restricted access"
                        width={72}
                        height={72}
                        draggable={false}
                        className="mx-auto mb-4" />

                    <h5 className="color-black e-poppins-semi-bold e-font-20 mb-3">
                        You're in restricted mode
                    </h5>

                    <p className="color-outer-space e-poppins-regular e-font-16 line-height-24px margin-32px-bottom">
                        {
                            isRejected
                                ? "Your KYC was rejected. Please review your details and re-submit to continue with your account setup."
                                : kycSubmitted
                                    ? "Your KYC is complete and your account is being activated. It will be fully active once you set your MPIN, which you can do from the login flow."
                                    : "You haven't finished your account setup yet. Please complete your KYC to continue. You'll be able to set your MPIN and unlock the full application at your next login."
                        }
                    </p>

                    <div className="d-flex justify-content-center">
                        {
                            kycSubmitted ? (
                                <GradientButton label="Log out"
                                    className="px-5 py-2 e-font-16"
                                    handleClick={_handleLogout} />
                            ) : (
                                <GradientButton label={isRejected ? "Review KYC" : "Complete KYC"}
                                    className="px-5 py-2 e-font-16"
                                    handleClick={_handleCompleteKyc} />
                            )
                        }
                    </div>
                </div>
            </div>
        </div>
    );
}
